import type { OptimusSchemas } from '~/clients/optimus-client'

export type SurgeonWaitingListResult = OptimusSchemas['SurgeonWaitingListResult']
export type PredictedWaitingListResult = {
    isLoading: boolean
    isError: boolean

    // number of patients on DIPS waiting list today
    currentUnscheduledWaitingList: number

    // number of 'bestilte' (booked) surgeries from today (until infinity). Used when showing current waiting list to compensate for surgeries that are booked ahead of the month we are predicting for.
    futureBookedSurgeries: number
    // number of 'bestilte' (booked) surgeries from 1st of month until end of month
    alreadyBookedSurgeriesThisMonth: number

    increaseUntilThisMonth: number
    availableCapacityUntilThisMonth: number

    // waiting list at start of month (current + futureBookedSurgeries (because we compensate for the surgeries that are booked ahead of the month we are predicting for) + increase - decrease)
    // e.g. if a surgeon has 10 patients on current waiting list and we look at month of February and a new patient is booked for March,
    // then the waiting list at the start of February should still be 10.
    // But since the waiting list only counts patients without a booking we need to add future booked surgeries too.
    // Otherwise, taking a patient from the waiting list and putting them on a planned surgery slot would reduce the predicted waiting list, which doesn't make any sense.
    waitingListStartOfMonth: number

    increaseThisMonth: number
    availableCapacityThisMonth: number

    // waiting list at end of month (start of month + increase - decrease)
    waitingListEndOfMonth: number

    // estimated number of surgeries performed this month
    totalPredictedSurgeriesThisMonth: number
    // whether the predicted waiting list is below 0 (used to be -10 but we changed it recently)
    isPredictedBelowSafeLevels: boolean
}

export type MapSurgeonWaitingListPredictionToPredictedWaitingListResultOptions = {
    predictionUntilStartOfMonth: SurgeonWaitingListResult
    predictionThisMonth: SurgeonWaitingListResult
    currentUnscheduledWaitingList: number
    futureBookedSurgeries: number
}

export function calculateSurgeonWaitingListPrediction({
    predictionUntilStartOfMonth,
    predictionThisMonth,
    currentUnscheduledWaitingList,
    futureBookedSurgeries,
}: MapSurgeonWaitingListPredictionToPredictedWaitingListResultOptions): PredictedWaitingListResult {
    const availableCapacityUntilThisMonth = predictionUntilStartOfMonth.outgoing_surgeries_unfilled.value
    const increaseUntilThisMonth = predictionUntilStartOfMonth.incoming_surgeries.value

    const increaseThisMonth = predictionThisMonth.incoming_surgeries.value

    const alreadyBookedSurgeriesThisMonth = predictionThisMonth.outgoing_surgeries.value - predictionThisMonth.outgoing_surgeries_unfilled.value
    const totalPredictedSurgeriesThisMonth = predictionThisMonth.outgoing_surgeries.value

    // futureBookedSurgeries do not include surgeries until start of month. Thus we only subtract available capacity until start of month.
    // (predictionUntilStartOfMonth.outgoing_surgeries would include booked surgeries as well, and would result in subtracting too much)
    const waitingListStartOfMonth = currentUnscheduledWaitingList + futureBookedSurgeries + increaseUntilThisMonth - availableCapacityUntilThisMonth
    const waitingListEndOfMonth = waitingListStartOfMonth - totalPredictedSurgeriesThisMonth + increaseThisMonth

    return {
        isLoading: false,
        isError: false,
        currentUnscheduledWaitingList,
        futureBookedSurgeries,
        alreadyBookedSurgeriesThisMonth,
        availableCapacityUntilThisMonth: predictionUntilStartOfMonth.outgoing_surgeries_unfilled.value,
        increaseUntilThisMonth,
        waitingListStartOfMonth,
        availableCapacityThisMonth: predictionThisMonth.outgoing_surgeries_unfilled.value,
        increaseThisMonth,
        waitingListEndOfMonth,
        totalPredictedSurgeriesThisMonth,
        isPredictedBelowSafeLevels: waitingListEndOfMonth < 0,
    }
}
